import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { withTranslation } from "react-i18next";
import styles from "./OptIn.module.scss";
import { Button, Grid, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { styled } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import { OPT_IN } from "../../../common/Constants";
import PendingActionPrompt from "../../../shared/pendingActionPrompt/PendingActionPrompt";
import HasPermissionTo from "../../../common/HasPermissionTo";
import DealerPreferencesClient from "../../../shared/clients/DealerPreferencesClient";
import MarketPreferencesClient from "../../../shared/clients/MarketPreferencesClient";
import { IS_SWISS_MARKET } from "../../../utils/EmpUtil";

const StyledRadio = styled(Radio)({
  color: "#102b4e",
  "&.Mui-checked": {
    color: "#102b4e",
  },
});

function OptIn({ user, hideOrShow, getPendingActions, isMainViewingSDLR, t, ...restProps }) {
  const [vmStatus, setVmStatus] = useState("");
  const [aldStatus, setAldStatus] = useState("");
  const [gpisStatus, setGpisStatus] = useState("");
  const [renderPage, setRenderPage] = useState(false);
  const [aldCupidId, setAldCupidId] = useState(undefined);
  const [optInPendingActionPrompt, setOptInPendingActionPrompt] = useState(false);

  const copyOptInPreferencesRef = useRef(null);
  const optInPAStatusForDealersRef = useRef([]);
  const optInDisablePAPromptRef = useRef(false);

  const hasALDPricing = useMemo(() =>
      user.permissions.rolePermissions.hasOwnProperty("aldPricing:view"),
    [user.permissions.rolePermissions]
  );

  const hasGPISPricing = useMemo(() =>
      user.permissions.rolePermissions.hasOwnProperty("gpisPricing:view"),
    [user.permissions.rolePermissions]
  );

  const hasVMPricing = useMemo(() =>
      user.permissions.rolePermissions.hasOwnProperty("variableMarketing:view"),
    [user.permissions.rolePermissions]
  );

  const onboardedDealers = useMemo(() =>
      user.getOnboardedDealerGroup(true),
    [user]
  );

  const handleOptInPendingActionPrompt = useCallback(() => {
    setOptInPendingActionPrompt(prev => !prev);
  }, []);

  const handleOptInChange = useCallback((e) => {
    const { name, value } = e.target;
    switch (name) {
      case "vmStatus":
        setVmStatus(value);
        break;
      case "aldStatus":
        setAldStatus(value);
        break;
      case "gpisStatus":
        setGpisStatus(value);
        break;
      default:
        break;
    }
  }, []);

  const getAldCupidId = useCallback(() => {
    MarketPreferencesClient.getAldCupidId(user).then((response) => {
      setAldCupidId(response.result.aldCupidId);
    });
  }, [user]);

  const extractResponse = useCallback((response, type) => {
    let status;
    if (response === "OPT_IN") {
      if (type === "vm") status = "vmYes";
      else if (type === "ald") status = "aldYes";
      else if (type === "gpis") status = "gpisYes";
    } else if (response === "OPT_OUT") {
      if (type === "vm") status = "vmNo";
      else if (type === "ald") status = "aldNo";
      else if (type === "gpis") status = "gpisNo";
    } else if (response === "PENDING" || response === "") {
      status = "";
    }
    return status;
  }, []);

  const getAllOptInStatus = useCallback(() => {
    DealerPreferencesClient.getOptInPreferences(user).then((response) => {
      const vmStatusValue = extractResponse(response.variableMarketingStatus, "vm");
      const aldStatusValue = extractResponse(response.aldPricingStatus, "ald");
      const gpisStatusValue = extractResponse(response.gpisPricingStatus, "gpis");

      copyOptInPreferencesRef.current = {
        vmStatus: vmStatusValue,
        aldStatus: aldStatusValue,
        gpisStatus: gpisStatusValue,
      };

      setVmStatus(vmStatusValue);
      setAldStatus(aldStatusValue);
      setGpisStatus(gpisStatusValue);
      setRenderPage(true);
    });
  }, [user, extractResponse]);

  const applyOptInPreferences = useCallback((optInRequest) => {
    hideOrShow(true);
    DealerPreferencesClient.updateAllOptInPreferences(user, optInRequest)
      .then(() => {
        getPendingActions().then(() => {
          determineOPTInPAStatus();
          getAllOptInStatus();
        });
      })
      .finally(() => {
        hideOrShow(false);
      });
  }, [hideOrShow, user, getPendingActions, getAllOptInStatus]);

  const updateRequestBody = useCallback((commonIds) => {
    const optInRequest = {};
    commonIds.forEach((commonId) => {
      const dealerPreference = {};
      if (vmStatus !== copyOptInPreferencesRef.current.vmStatus)
        dealerPreference.variableMarketingStatus = vmStatus === "vmYes" ? "OPT_IN" : "OPT_OUT";
      if (aldStatus !== copyOptInPreferencesRef.current.aldStatus)
        dealerPreference.aldPricingStatus = aldStatus === "aldYes" ? "OPT_IN" : "OPT_OUT";
      if (gpisStatus !== copyOptInPreferencesRef.current.gpisStatus)
        dealerPreference.gpisPricingStatus = gpisStatus === "gpisYes" ? "OPT_IN" : "OPT_OUT";

      const formattedCommonId = commonId.toString().split("%7C").join("|");
      optInRequest[formattedCommonId] = dealerPreference;
    });
    applyOptInPreferences(optInRequest);
  }, [vmStatus, aldStatus, gpisStatus, applyOptInPreferences]);

  const getAllIds = useCallback((pendingActionStatus, commonId = false) => {
    return pendingActionStatus.map((dealer) => {
      return commonId ? dealer.commonId : dealer.dealerId;
    });
  }, []);

  const getSelectiveIds = useCallback((pendingActionStatus, commonId = false) => {
    return pendingActionStatus
      .filter((dealer) => !dealer.pendingActionStatus)
      .map((dealer) => {
        return commonId ? dealer.commonId : dealer.dealerId;
      });
  }, []);

  const determineOPTInPAStatus = useCallback(() => {
    optInPAStatusForDealersRef.current = user.getPendingActionStatusForDealers(
      user.pendingActions,
      OPT_IN
    );
    optInDisablePAPromptRef.current = optInPAStatusForDealersRef.current.length === 0;
  }, [user]);

  const optInPAPromptApplyAll = useCallback(() => {
    updateRequestBody(getAllIds(optInPAStatusForDealersRef.current[0], true));
    handleOptInPendingActionPrompt();
  }, [updateRequestBody, getAllIds, handleOptInPendingActionPrompt]);

  const optInPAPromptApplySelective = useCallback(() => {
    updateRequestBody(getSelectiveIds(optInPAStatusForDealersRef.current[0], true));
    handleOptInPendingActionPrompt();
  }, [updateRequestBody, getSelectiveIds, handleOptInPendingActionPrompt]);

  const determineApplyDisabled = useCallback(() => {
    let applyDisabled = true;
    if (
      (aldStatus !== "" || !hasALDPricing) &&
      (gpisStatus !== "" || !hasGPISPricing) &&
      (vmStatus !== "" || !hasVMPricing)
    ) {
      applyDisabled =
        aldStatus === copyOptInPreferencesRef.current?.aldStatus &&
        vmStatus === copyOptInPreferencesRef.current?.vmStatus &&
        gpisStatus === copyOptInPreferencesRef.current?.gpisStatus;
    }
    return applyDisabled;
  }, [aldStatus, vmStatus, gpisStatus, hasALDPricing, hasGPISPricing, hasVMPricing]);

  const saveOptIn = useCallback(() => {
    if (
      user.showAll &&
      onboardedDealers.length > 1 &&
      !optInDisablePAPromptRef.current
    ) {
      handleOptInPendingActionPrompt();
    } else {
      updateRequestBody(user.getCommonIdsWithoutSDLR().split(","));
    }
  }, [user, onboardedDealers.length, handleOptInPendingActionPrompt, updateRequestBody]);

  const getOptInPanel = useCallback((
    prompt,
    secondPrompt,
    name,
    value,
    yesId,
    noId,
    onClick,
    permissions
  ) => {
    return (
      <div className="pagePaddingTop">
        <Grid container className={`${styles.panel} pagePadding`}>
          <Grid item xs={10}>
            <div className={styles.prompt}>{prompt}</div>
            <span className={styles.secondaryPrompt}>{secondPrompt}</span>
          </Grid>
          <Grid item xs={2}>
            <HasPermissionTo
              perform={[permissions]}
              condition={!isMainViewingSDLR(user)}
              permissions={user.permissions.rolePermissions}
              render={() => (
                <FormControl
                  component="fieldset"
                  className={styles.radioContainer}
                >
                  <RadioGroup
                    name={name}
                    label={name}
                    value={value}
                    row
                    onChange={onClick}
                  >
                    <FormControlLabel
                      value={yesId}
                      control={<StyledRadio />}
                      className={styles.labels}
                      data-testid={yesId}
                      label={t("OptIn.yes")}
                    />
                    <FormControlLabel
                      value={noId}
                      control={<StyledRadio />}
                      className={styles.labels}
                      data-testid={noId}
                      label={t("OptIn.no")}
                    />
                  </RadioGroup>
                </FormControl>
              )}
              noRender={() =>
                value.toLowerCase().includes("yes") ? (
                  <div className={styles.viewOnly}>
                    {t("OptIn.yes")}
                  </div>
                ) : value.toLowerCase().includes("no") ? (
                  <div className={styles.viewOnly}>
                    {t("OptIn.no")}
                  </div>
                ) : (
                  value === "" && (
                    <div className={styles.viewOnly}>
                      {t("OptIn.pending")}
                    </div>
                  )
                )
              }
            />
          </Grid>
        </Grid>
      </div>
    );
  }, [user, isMainViewingSDLR, t]);

  useEffect(() => {
    hideOrShow(true);
    determineOPTInPAStatus();
    getAldCupidId();
    getAllOptInStatus();
    hideOrShow(false);
  }, [hideOrShow, determineOPTInPAStatus, getAldCupidId, getAllOptInStatus]);

  const applyDisabled = renderPage && determineApplyDisabled();

  return (
    renderPage && (
      <div>
        <div className={styles.subHeader}>
          {t("OptIn.subHeader")}
        </div>

        <HasPermissionTo
          perform={["variableMarketing:view"]}
          permissions={user.permissions.rolePermissions}
          render={() =>
            getOptInPanel(
              t("VariableMarketing.prompt"),
              t("VariableMarketing.secondPrompt"),
              "vmStatus",
              vmStatus,
              "vmYes",
              "vmNo",
              handleOptInChange,
              "variableMarketing:write"
            )
          }
        />

        <HasPermissionTo
          perform={["aldPricing:view"]}
          permissions={user.permissions.rolePermissions}
          render={() => (
            <>
              {getOptInPanel(
                t("ALDPricing.prompt"),
                t("ALDPricing.secondPrompt"),
                "aldStatus",
                aldStatus,
                "aldYes",
                "aldNo",
                handleOptInChange,
                "aldPricing:write"
              )}

              {aldStatus === "aldYes" && (
                <div
                  className={`${styles.aldStatusDropdown} pagePaddingLeft pagePaddingRight`}
                >
                  <div className={styles.cupidIdWrapper}>
                    <span className={styles.cupidIdLabel}>
                      {t("ALDPricing.aldCupidId")}
                    </span>
                    <span className={styles.cupidIdValue}>
                      {aldCupidId}
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        />

        <HasPermissionTo
          perform={["gpisPricing:view"]}
          permissions={user.permissions.rolePermissions}
          render={() => (
            <>
              {getOptInPanel(
                t("OptIn.gpisHeader"),
                t("OptIn.gpisSubHeader"),
                "gpisStatus",
                gpisStatus,
                "gpisYes",
                "gpisNo",
                handleOptInChange,
                "gpisPricing:write"
              )}
            </>
          )}
        />

        <HasPermissionTo
          perform={["optIn:write"]}
          permissions={user.permissions.rolePermissions}
          condition={!isMainViewingSDLR(user)}
          render={() => (
            <div className={styles.actionSection}>
              <Button
                data-testid="applyOptionsBtn"
                variant="contained"
                onClick={saveOptIn}
                disabled={applyDisabled}
                className={
                  applyDisabled ? styles.disableButton : styles.applyBtn
                }
                component="button"
              >
                <span>
                  {applyDisabled
                    ? IS_SWISS_MARKET(user.market)
                      ? t("OptIn.disabledApplySingular")
                      : t("OptIn.disabledApply")
                    : t("OptIn.apply")}
                </span>
              </Button>
            </div>
          )}
        />

        <PendingActionPrompt
          user={user}
          hideOrShow={hideOrShow}
          getPendingActions={getPendingActions}
          isMainViewingSDLR={isMainViewingSDLR}
          t={t}
          {...restProps}
          disabled={optInDisablePAPromptRef.current}
          dealersActioned={
            optInDisablePAPromptRef.current
              ? []
              : optInPAStatusForDealersRef.current[0]?.filter(
              (dealer) => dealer.pendingActionStatus
            ) || []
          }
          dealersNotActioned={
            optInDisablePAPromptRef.current
              ? []
              : optInPAStatusForDealersRef.current[0]?.filter(
              (dealer) => !dealer.pendingActionStatus
            ) || []
          }
          title={t("PendingActionPrompt.gpisPricing")}
          buttonTitle={t("PendingActionPrompt.gpisPricingButton")}
          onClose={handleOptInPendingActionPrompt}
          open={optInPendingActionPrompt}
          applyAll={optInPAPromptApplyAll}
          applySome={optInPAPromptApplySelective}
        />
      </div>
    )
  );
}

export default withTranslation("emp")(OptIn);
